const MINUTE_IN_SECONDS = 60;
const HOUR_IN_SECONDS = 3600;

const UNIT_NAMES = {
  hours: ['hour', 'hours'],
  minutes: ['minute', 'minutes'],
  seconds: ['second', 'seconds']
};

// simple pluralization.
function pluralize(term, value) {
  return value === 1 ? term[0] : term[1];
}

// handles repetitive formatting logic
function formatTimeUnit(value = 0, unit) {
  return value > 0 ? `${value} ${pluralize(unit, value)}` : '';
}

/**
 * Round number to a given number decimal places
 * Note, unlike toFixed, this does not add unnecessary decimals
 * @param {number} number
 * @param {number} places
 * @returns {number}
 * @private
 */
function toFloat(number, places) {
  return +`${Math.round(`${number}e+${places}`)}${`e-${places}`}`;
}

/**
 * Convert a value from seconds to a given unit
 * @param {number} valueInSeconds
 * @param {string} [unit]
 * @returns {number|undefined}
 */
function convertToUnit(valueInSeconds, unit = 'second') {
  const denominator =
    {
      hours: HOUR_IN_SECONDS,
      minutes: MINUTE_IN_SECONDS
    }[unit] || 1;

  if (valueInSeconds) {
    return toFloat(valueInSeconds / denominator, 2);
  }
  return undefined;
}

/**
 * Convert to a human readable string, e.g. '10 - 12 minutes'
 * @param {number} minValue
 * @param {number} maxValue
 * @param {string} unit
 * @returns {string}
 */
function asHumanString(minValue, maxValue, unit = 'seconds') {
  const name = UNIT_NAMES[unit];

  if (unit === 'seconds') {
    const minValueTxt = formatTimeUnit(minValue, name);
    const maxValueTxt = formatTimeUnit(maxValue, name);

    if (minValue && maxValue) {
      return `${minValueTxt} to ${maxValueTxt}`;
    }
    return `${minValue} ${pluralize(name, minValue)}`;
  }

  const secondUnit = unit === 'hours' ? 'minutes' : 'seconds';
  const secondName = UNIT_NAMES[secondUnit];

  const minQuotient = parseInt(minValue / 1, 10);
  const minQuotientTxt = formatTimeUnit(minQuotient, name);

  const minRemainder = Math.round((minValue % 1) * 60);
  const minRemainderTxt = formatTimeUnit(minRemainder, secondName);

  const maxQuotient = parseInt(maxValue / 1, 10);
  const maxQuotientTxt = formatTimeUnit(maxQuotient, name);

  const maxRemainder = Math.round((maxValue % 1) * 60);
  const maxRemainderTxt = formatTimeUnit(maxRemainder, secondName);

  if (minValue && maxValue) {
    return [
      minQuotientTxt,
      minRemainderTxt,
      'to',
      maxQuotientTxt,
      maxRemainderTxt
    ]
      .filter(Boolean)
      .join(' ');
  }

  return [minQuotientTxt, minRemainderTxt].filter(Boolean).join(' ');
}

/**
 * Convert to an ISO 8601 string
 * @param {number} value
 * @param {string} [unit]
 * @returns {number|undefined}
 */
function asTimeString(value, unit = 'seconds') {
  if (value) {
    return `PT${value}${unit.charAt(0).toUpperCase()}`;
  }
  return undefined;
}

/**
 * Converts an object containing min and max (in seconds) into the components
 * required by the front-end
 * @param {object}
 * @returns {object}
 */
export function convertTimeToComponents(obj) {
  if (!obj || (obj.min === null && obj.max === null)) {
    return undefined;
  }

  const { min = 0, max = 0 } = obj;
  const upper = Math.max(min, max);

  let unit = '';
  if (upper >= HOUR_IN_SECONDS) {
    unit = 'hours';
  } else if (upper >= MINUTE_IN_SECONDS) {
    unit = 'minutes';
  } else {
    unit = 'seconds';
  }

  const unitMin = convertToUnit(min, unit);
  const unitMax = convertToUnit(max, unit);

  return {
    min: unitMin,
    max: unitMax,
    isoMin: asTimeString(unitMin, unit),
    isoMax: asTimeString(unitMax, unit),
    humanString: asHumanString(unitMin, unitMax, unit),
    unit
  };
}

/**
 * Converts an object of components into a simple structure required by the API
 * @param {object}
 * @returns {object|null}
 */
export function convertComponentsToTime(obj = {}) {
  const multiplier =
    {
      hours: HOUR_IN_SECONDS,
      minutes: MINUTE_IN_SECONDS
    }[obj.unit] || 1;

  if (!obj.min && !obj.max) {
    return null;
  }

  return {
    min: obj.min ? Math.floor(obj.min * multiplier) : null,
    max: obj.max ? Math.floor(obj.max * multiplier) : null
  };
}

export default {
  convertTimeToComponents,
  convertComponentsToTime
};
